/**
 * Floating, draggable picture-in-picture window for the active LiveKit session.
 * Mounted once globally in App.js. Renders only when a live session is active.
 * Uses the low-level livekit-client Room API (no LiveKitRoom wrapper) so it works
 * independently of which page the user is currently on.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Track } from 'livekit-client';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Maximize2, Minimize2, Users, GripHorizontal, Expand, Shrink } from 'lucide-react';
import { useLiveVideo } from '../../context/LiveVideoContext';

function attachTrack(track, videoEl) {
  if (!track || !videoEl) return () => {};
  try {
    track.attach(videoEl);
  } catch { /* noop */ }
  return () => {
    try { track.detach(videoEl); } catch { /* noop */ }
  };
}

function findCameraPublication(participant) {
  if (!participant) return null;
  // localParticipant: trackPublications Map
  const pubs = participant.trackPublications || participant.videoTracks;
  if (!pubs) return null;
  for (const pub of pubs.values()) {
    if (pub.source === Track.Source.Camera || pub.kind === Track.Kind.Video) {
      return pub;
    }
  }
  return null;
}

const MIN_W = 180;
const MIN_H = 135;
const MAX_W = 720;
const MAX_H = 540;
const SNAP_MARGIN = 16; // px from edge for snapped position
const SNAP_TRIGGER = 80; // when drop is within this many px of an edge, snap
const PREFS_KEY = 'as-live-pip-prefs-v1';

// Size presets cycled by the explicit S/M/L button. Tuned so "S" gets out of
// the way of the workout content — most users want Small while exercising.
const SIZE_PRESETS = [
  { label: 'S', w: 180, h: 135 },
  { label: 'M', w: 280, h: 210 },
  { label: 'L', w: 440, h: 330 },
];

// PiP size + last-dragged corner are persisted to localStorage so the user's
// preferred layout survives refreshes. This is PURELY non-sensitive UI state
// (x, y, w, h, corner snap) — no auth tokens, no personal data, no session
// identifiers. Stored locally so other devices/sessions stay isolated.
function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p !== 'object' || !p) return null;
    return p;
  } catch (e) {
    console.debug('FloatingLiveVideo loadPrefs failed:', e?.message);
    return null;
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch (e) {
    // Quota exceeded or storage disabled — prefs simply won't persist, which
    // is a purely cosmetic degradation, so we log and carry on.
    console.debug('FloatingLiveVideo savePrefs failed:', e?.message);
  }
}

function snapCorner({ x, y, w, h }) {
  // Returns the nearest corner if within trigger; else returns the input pos.
  const W = window.innerWidth;
  const H = window.innerHeight;
  const distLeft = x;
  const distRight = W - (x + w);
  const distTop = y;
  const distBottom = H - (y + h);

  const horizontal = distLeft <= distRight ? 'left' : 'right';
  const vertical = distTop <= distBottom ? 'top' : 'bottom';
  const minH = Math.min(distLeft, distRight);
  const minV = Math.min(distTop, distBottom);

  if (minH > SNAP_TRIGGER && minV > SNAP_TRIGGER) {
    return { x, y, snapped: false };
  }
  const newX = horizontal === 'left' ? SNAP_MARGIN : W - w - SNAP_MARGIN;
  const newY = vertical === 'top' ? SNAP_MARGIN : H - h - SNAP_MARGIN;
  return { x: newX, y: newY, snapped: true };
}

export default function FloatingLiveVideo() {
  const { room, session, connected, endSession, toggleMic, toggleCam, isMicMuted, isCamOff } = useLiveVideo();
  const location = useLocation();
  // Suppress the floating PiP on pages that render their own inline video stage
  const suppressOnPath = /^\/live-workout\/[^/]+\/(room|recordings)/.test(location.pathname);

  const initialPrefs = (typeof window !== 'undefined' ? loadPrefs() : null) || {};
  const [minimized, setMinimized] = useState(!!initialPrefs.minimized);
  const [fullscreen, setFullscreen] = useState(false);
  const [size, setSize] = useState(() => ({
    w: Math.min(Math.max(initialPrefs.w ?? SIZE_PRESETS[0].w, MIN_W), MAX_W),
    h: Math.min(Math.max(initialPrefs.h ?? SIZE_PRESETS[0].h, MIN_H), MAX_H),
  }));
  const [pos, setPos] = useState(() => {
    const w = Math.min(Math.max(initialPrefs.w ?? SIZE_PRESETS[0].w, MIN_W), MAX_W);
    const h = Math.min(Math.max(initialPrefs.h ?? SIZE_PRESETS[0].h, MIN_H), MAX_H);
    if (typeof initialPrefs.x === 'number' && typeof initialPrefs.y === 'number') {
      return {
        x: Math.max(0, Math.min(window.innerWidth - 80, initialPrefs.x)),
        y: Math.max(0, Math.min(window.innerHeight - 80, initialPrefs.y)),
      };
    }
    return { x: window.innerWidth - w - SNAP_MARGIN, y: window.innerHeight - h - SNAP_MARGIN };
  });

  // Persist size/pos/minimized whenever they change — but only when NOT in
  // fullscreen, so reloading after a fullscreen session restores the previous
  // floating size, not the entire viewport.
  useEffect(() => {
    if (fullscreen) return;
    savePrefs({ x: pos.x, y: pos.y, w: size.w, h: size.h, minimized });
  }, [pos, size, minimized, fullscreen]);

  // dragRef tracks both move & resize gestures
  const dragRef = useRef({ kind: null, offsetX: 0, offsetY: 0, startW: 0, startH: 0, startX: 0, startY: 0 });
  const remoteVideoRef = useRef(null);
  const selfVideoRef = useRef(null);

  // Clamp position when window shrinks. Also: keep the fullscreen rect glued
  // to viewport size on rotation/resize so it stays truly full-bleed.
  useEffect(() => {
    const onResize = () => {
      if (fullscreen) {
        setSize({ w: window.innerWidth, h: window.innerHeight });
        setPos({ x: 0, y: 0 });
        return;
      }
      setPos((p) => ({
        x: Math.max(0, Math.min(p.x, window.innerWidth - 80)),
        y: Math.max(0, Math.min(p.y, window.innerHeight - 80)),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fullscreen]);

  // Attach remote camera track. The parent's bumpTick re-render is what
  // triggers re-evaluation when participants/tracks change.
  useEffect(() => {
    if (!room || !remoteVideoRef.current) return undefined;
    let cleanup = () => {};
    const remotes = Array.from(room.remoteParticipants.values());
    if (remotes.length > 0) {
      const pub = findCameraPublication(remotes[0]);
      if (pub?.track) cleanup = attachTrack(pub.track, remoteVideoRef.current);
    }
    return cleanup;
  });

  // Attach local camera track (small self preview). Same re-render contract.
  useEffect(() => {
    if (!room || !selfVideoRef.current) return undefined;
    const pub = findCameraPublication(room.localParticipant);
    if (pub?.track) return attachTrack(pub.track, selfVideoRef.current);
    return undefined;
  });

  // Track latest size in a ref so the global onUp can snap correctly
  const sizeRef = useRef(size);
  useEffect(() => { sizeRef.current = size; }, [size]);

  // Double-tap to maximize / restore
  const lastTapRef = useRef(0);
  const restoreRef = useRef(null); // {w, h, x, y} when maximized; null when at user size

  const toggleMaximize = () => {
    // True full-screen: cover the entire viewport. Restored via the same toggle
    // (button or double-tap) — preserved size/pos kept in restoreRef.
    if (fullscreen) {
      const r = restoreRef.current || { w: SIZE_PRESETS[0].w, h: SIZE_PRESETS[0].h, x: window.innerWidth - SIZE_PRESETS[0].w - SNAP_MARGIN, y: window.innerHeight - SIZE_PRESETS[0].h - SNAP_MARGIN };
      restoreRef.current = null;
      setFullscreen(false);
      setSize({ w: r.w, h: r.h });
      setPos({ x: r.x, y: r.y });
    } else {
      restoreRef.current = { w: size.w, h: size.h, x: pos.x, y: pos.y };
      setFullscreen(true);
      setSize({ w: window.innerWidth, h: window.innerHeight });
      setPos({ x: 0, y: 0 });
    }
  };

  // Cycle the PiP through S → M → L → S. Picks the first preset whose width
  // is greater than the current width (with a small tolerance) and falls back
  // to S. After resizing, re-snap to the same corner the user was already in.
  const cyclePreset = () => {
    if (fullscreen) {
      // Cycling implies the user wants a regular floating size — exit FS first.
      setFullscreen(false);
      restoreRef.current = null;
    }
    const cur = fullscreen ? SIZE_PRESETS[SIZE_PRESETS.length - 1].w : size.w;
    const next = SIZE_PRESETS.find((p) => p.w > cur + 4) || SIZE_PRESETS[0];
    setSize({ w: next.w, h: next.h });
    // Re-snap: pick the corner closest to the current position so the window
    // doesn't drift off-screen on a resize.
    const W = window.innerWidth;
    const H = window.innerHeight;
    const horizLeft = pos.x + size.w / 2 < W / 2;
    const vertTop = pos.y + size.h / 2 < H / 2;
    setPos({
      x: horizLeft ? SNAP_MARGIN : Math.max(0, W - next.w - SNAP_MARGIN),
      y: vertTop ? SNAP_MARGIN : Math.max(0, H - next.h - SNAP_MARGIN),
    });
  };

  // Label for the preset BUTTON: shows the size you'll get when you tap it
  // (i.e. the NEXT preset in the cycle), not the current size.
  const nextPresetLabel = (() => {
    if (fullscreen) return SIZE_PRESETS[0].label; // exiting FS lands at S
    const next = SIZE_PRESETS.find((p) => p.w > size.w + 4) || SIZE_PRESETS[0];
    return next.label;
  })();

  // Global pointer listeners — must run on every render before any early return
  // (React hooks rules) so they're attached even when the window is hidden.
  // Uses pointer events so it works for both mouse and touch.
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.kind) return;
      if (d.kind === 'move') {
        const x = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - d.offsetX));
        const y = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - d.offsetY));
        setPos({ x, y });
      } else if (d.kind === 'resize') {
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        const w = Math.max(MIN_W, Math.min(MAX_W, d.startW + dx));
        const h = Math.max(MIN_H, Math.min(MAX_H, d.startH + dy));
        setSize({ w, h });
      }
      e.preventDefault?.();
    };
    const onUp = () => {
      const d = dragRef.current;
      if (!d.kind) return;
      d.kind = null;
      // After move or resize, snap to the nearest corner if close enough.
      setPos((cur) => {
        const { w, h } = sizeRef.current;
        const { x, y } = snapCorner({ x: cur.x, y: cur.y, w, h });
        return { x, y };
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  if (!session || !connected || suppressOnPath) return null;

  const startMove = (e) => {
    if (e.target.closest('[data-no-drag]')) return;
    // While in fullscreen, drag and reposition are disabled (window covers
    // the whole viewport). Double-tap still toggles between FS and the user's
    // saved size.
    if (fullscreen) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        lastTapRef.current = 0;
        toggleMaximize();
        e.preventDefault();
        return;
      }
      lastTapRef.current = now;
      return;
    }
    // Double-tap detection — toggle maximize on quick repeat tap.
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      lastTapRef.current = 0;
      toggleMaximize();
      e.preventDefault();
      return;
    }
    lastTapRef.current = now;
    dragRef.current = { kind: 'move', offsetX: e.clientX - pos.x, offsetY: e.clientY - pos.y, startW: 0, startH: 0, startX: 0, startY: 0 };
    e.preventDefault();
  };

  const startResize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (fullscreen) return; // resize grip hidden in FS, but guard anyway
    dragRef.current = { kind: 'resize', offsetX: 0, offsetY: 0, startW: size.w, startH: size.h, startX: e.clientX, startY: e.clientY };
  };

  // Plain function (not a hook) — only called from event handlers.
  const snapToCorner = (corner) => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const x = corner.endsWith('left') ? SNAP_MARGIN : W - size.w - SNAP_MARGIN;
    const y = corner.startsWith('top') ? SNAP_MARGIN : H - size.h - SNAP_MARGIN;
    setPos({ x, y });
  };

  const micMuted = isMicMuted();
  const camOff = isCamOff();

  const remoteParticipants = room ? Array.from(room.remoteParticipants.values()) : [];
  const remoteName = remoteParticipants[0]?.name || remoteParticipants[0]?.identity || 'Your buddy';
  const hasRemoteVideo = remoteParticipants.length > 0 && !!findCameraPublication(remoteParticipants[0])?.track;

  if (minimized) {
    return (
      <div
        className="fixed z-[70] bg-black border border-red-500/60 rounded-full shadow-2xl flex items-center gap-2 pl-3 pr-1 py-1 cursor-move touch-none"
        style={{ left: pos.x, top: pos.y }}
        onPointerDown={startMove}
        data-testid="floating-live-min"
      >
        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-white text-xs font-heading uppercase tracking-wider">Live · {remoteName}</span>
        <button
          onClick={() => setMinimized(false)}
          className="p-1.5 rounded-full hover:bg-white/10 text-white"
          data-no-drag
          aria-label="expand"
          data-testid="floating-live-expand"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`fixed z-[70] bg-black ${fullscreen ? 'rounded-none' : 'rounded-lg'} shadow-2xl ${fullscreen ? '' : 'border border-white/10'} overflow-hidden select-none touch-none`}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h, cursor: fullscreen ? 'default' : 'move' }}
      onPointerDown={startMove}
      data-testid="floating-live-window"
    >
      {/* Drag header */}
      <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-2 py-1 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
        <div className="flex items-center gap-1.5 pointer-events-auto">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-white text-[10px] font-heading uppercase tracking-wider">Live</span>
        </div>
        <div className="flex items-center gap-1 pointer-events-auto" data-no-drag>
          {/* Snap-to-corner quick buttons (hidden in fullscreen) */}
          <button
            onClick={() => snapToCorner('top-left')}
            className={`${fullscreen ? 'hidden' : 'hidden sm:block'} p-1 rounded hover:bg-white/20 text-white/70 hover:text-white`}
            aria-label="snap top-left"
            data-testid="floating-live-snap-tl"
            title="Snap top-left"
          >
            <span className="block h-2 w-2 border-l-2 border-t-2 border-current" />
          </button>
          <button
            onClick={() => snapToCorner('top-right')}
            className={`${fullscreen ? 'hidden' : 'hidden sm:block'} p-1 rounded hover:bg-white/20 text-white/70 hover:text-white`}
            aria-label="snap top-right"
            data-testid="floating-live-snap-tr"
            title="Snap top-right"
          >
            <span className="block h-2 w-2 border-r-2 border-t-2 border-current" />
          </button>
          <button
            onClick={() => snapToCorner('bottom-left')}
            className={`${fullscreen ? 'hidden' : 'hidden sm:block'} p-1 rounded hover:bg-white/20 text-white/70 hover:text-white`}
            aria-label="snap bottom-left"
            data-testid="floating-live-snap-bl"
            title="Snap bottom-left"
          >
            <span className="block h-2 w-2 border-l-2 border-b-2 border-current" />
          </button>
          <button
            onClick={() => snapToCorner('bottom-right')}
            className={`${fullscreen ? 'hidden' : 'hidden sm:block'} p-1 rounded hover:bg-white/20 text-white/70 hover:text-white`}
            aria-label="snap bottom-right"
            data-testid="floating-live-snap-br"
            title="Snap bottom-right"
          >
            <span className="block h-2 w-2 border-r-2 border-b-2 border-current" />
          </button>
          {/* Explicit size cycle — solves the "video covers the workout" UX issue.
              Tapping cycles S → M → L → S so users can shrink without hunting for
              the corner-resize grip. Mobile-friendly target (24×20). */}
          <button
            onClick={cyclePreset}
            className="px-1.5 py-0.5 rounded hover:bg-white/20 text-white text-[10px] font-bold leading-none flex items-center justify-center min-w-[20px]"
            aria-label="cycle size"
            title={`Resize PiP (next: ${nextPresetLabel})`}
            data-testid="floating-live-size-cycle"
          >
            {nextPresetLabel}
          </button>
          {/* Explicit fullscreen toggle — covers the whole viewport (and back). */}
          <button
            onClick={toggleMaximize}
            className="p-1 rounded hover:bg-white/20 text-white"
            aria-label={fullscreen ? 'exit fullscreen' : 'enter fullscreen'}
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            data-testid="floating-live-fullscreen"
          >
            {fullscreen
              ? <Shrink className="h-3 w-3" />
              : <Expand className="h-3 w-3" />}
          </button>
          <button
            onClick={() => setMinimized(true)}
            className="p-1 rounded hover:bg-white/20 text-white"
            aria-label="minimize"
            data-testid="floating-live-minimize"
          >
            <Minimize2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Remote video fills the window */}
      {hasRemoteVideo ? (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover bg-zinc-900"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-900 text-center px-3">
          <Users className="h-6 w-6 text-muted-foreground mb-1" />
          <p className="text-[11px] text-white font-heading uppercase tracking-wider">Waiting for buddy</p>
          <p className="text-[10px] text-muted-foreground">{remoteName}</p>
        </div>
      )}

      {/* Self preview in corner */}
      <div
        className="absolute bottom-10 right-2 rounded overflow-hidden border border-white/40 bg-black"
        style={{ width: Math.max(48, Math.round(size.w * 0.18)), height: Math.max(64, Math.round(size.h * 0.32)) }}
      >
        {!camOff ? (
          <video ref={selfVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <VideoOff className="h-4 w-4" />
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-black/70 backdrop-blur-sm" data-no-drag>
        <button
          onClick={toggleMic}
          className={`p-1.5 rounded-full ${micMuted ? 'bg-red-600' : 'bg-white/10 hover:bg-white/20'}`}
          aria-label={micMuted ? 'unmute mic' : 'mute mic'}
          data-testid="floating-live-mic"
        >
          {micMuted ? <MicOff className="h-3.5 w-3.5 text-white" /> : <Mic className="h-3.5 w-3.5 text-white" />}
        </button>
        <button
          onClick={toggleCam}
          className={`p-1.5 rounded-full ${camOff ? 'bg-red-600' : 'bg-white/10 hover:bg-white/20'}`}
          aria-label={camOff ? 'turn camera on' : 'turn camera off'}
          data-testid="floating-live-cam"
        >
          {camOff ? <VideoOff className="h-3.5 w-3.5 text-white" /> : <Video className="h-3.5 w-3.5 text-white" />}
        </button>
        <button
          onClick={endSession}
          className="p-1.5 rounded-full bg-red-600 hover:bg-red-700"
          aria-label="end call"
          data-testid="floating-live-end"
        >
          <PhoneOff className="h-3.5 w-3.5 text-white" />
        </button>
      </div>

      {/* Resize grip — bottom-right corner. Hidden in fullscreen since the
          window already covers the whole viewport. */}
      {!fullscreen && (
        <div
          onPointerDown={startResize}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-20 flex items-end justify-end p-0.5 group"
          data-no-drag
          data-testid="floating-live-resize"
          aria-label="resize"
          role="separator"
        >
          <GripHorizontal className="h-3 w-3 text-white/40 group-hover:text-white rotate-45" />
        </div>
      )}
    </div>
  );
}
